import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Mail } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { createEncounter, getEncounterForAppointmentAndPet } from '../../utils/encounterApi';
import { AppointmentStatus } from '../../types/appointment';

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
  gender?: string;
  date_of_birth?: string;
  behavioral_tags?: {
    aggressive?: boolean;
    fearful?: boolean;
    go_slow?: boolean;
    caution?: boolean;
    demo?: boolean;
    notes?: string;
  };
}

interface Owner {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
}

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  duration_minutes: number;
  appointment_type: string;
  status: string;
  description?: string;
  notes?: string;
  pets?: Pet[];
  pet_owner?: Owner;
  pet_owner_id?: string;
  practice_id?: string;
}

interface PatientFlowViewModalProps {
  appointment: Appointment;
  onClose: () => void;
}

const BEHAVIORAL_TAG_COLORS = {
  aggressive: 'bg-orange-100 text-orange-800 border-2 border-orange-300',
  fearful: 'bg-blue-100 text-blue-800 border-2 border-blue-300',
  go_slow: 'bg-green-100 text-green-800 border-2 border-green-300',
  caution: 'bg-orange-100 text-orange-800 border-2 border-orange-300',
  demo: 'bg-blue-100 text-blue-800 border-2 border-blue-300',
};

export const PatientFlowViewModal: React.FC<PatientFlowViewModalProps> = ({
  appointment,
  onClose,
}) => {
  const navigate = useNavigate();
  const [encounterLoading, setEncounterLoading] = useState(false);

  const handlePetClick = async (petId: string) => {
    if (!appointment) return;
    
    setEncounterLoading(true);
    try {
      // Check if encounter already exists for this appointment and pet
      let encounter;
      try {
        encounter = await getEncounterForAppointmentAndPet(appointment.id, petId);
      } catch (err: any) {
        // Encounter doesn't exist (404), create it
        if (err.status === 404 || err.name === 'EncounterAPIError') {
          encounter = await createEncounter(petId, {
            appointment_id: appointment.id,
            pet_id: petId,
            owner_id: appointment.pet_owner?.id || '',
            practice_id: appointment.practice_id || '',
          });
        } else {
          throw err;
        }
      }
      
      // Navigate to encounter page with new URL structure
      onClose();
      navigate(`/pet/${petId}/encounter/${encounter.id}/intake`);
    } catch (err: any) {
      console.error('Failed to start encounter:', err);
      alert(err.message || err.detail || 'Failed to start encounter');
    } finally {
      setEncounterLoading(false);
    }
  };
  useEscapeKey(onClose, true);
  
  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const endDate = new Date(date.getTime() + appointment.duration_minutes * 60000);
      return `${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} – ${endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } catch {
      return dateString;
    }
  };

  const calculateAge = (dateOfBirth: string) => {
    try {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      const years = today.getFullYear() - birthDate.getFullYear();
      const months = today.getMonth() - birthDate.getMonth();
      
      if (years > 0) {
        const adjustedMonths = months < 0 ? 12 + months : months;
        return adjustedMonths > 0 ? `${years} years ${adjustedMonths} months` : `${years} years`;
      } else {
        const totalMonths = months + (years * 12);
        return totalMonths > 0 ? `${totalMonths} months` : 'Less than a month';
      }
    } catch {
      return 'Unknown age';
    }
  };

  const getActiveTags = (pet: Pet) => {
    const tags = [];
    if (pet.behavioral_tags?.aggressive) tags.push({ key: 'aggressive', label: 'Aggressive' });
    if (pet.behavioral_tags?.fearful) tags.push({ key: 'fearful', label: 'Fearful' });
    if (pet.behavioral_tags?.go_slow) tags.push({ key: 'go_slow', label: 'Go Slow' });
    if (pet.behavioral_tags?.caution) tags.push({ key: 'caution', label: 'Caution' });
    if (pet.behavioral_tags?.demo) tags.push({ key: 'demo', label: 'Demo' });
    return tags;
  };

  const owner = appointment.pet_owner;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[500] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">
              {appointment.title}
            </h2>
            <p className="text-sm text-gray-500 capitalize">{appointment.status.replace('_', ' ')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-4"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left Column - Patient Info */}
            <div className="space-y-6">
              {/* Pet Info */}
              {appointment.pets && appointment.pets.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Patient Information
                    </h3>
                  </div>
                  <div className="p-4 space-y-4">
                    {appointment.pets.map((pet) => {
                      const activeTags = getActiveTags(pet);
                      
                      return (
                        <div 
                          key={pet.id} 
                          className="border border-gray-200 rounded-lg p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            onClose();
                            navigate(`/pets/${pet.id}`);
                          }}
                        >
                          {/* Pet Header */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-lg font-bold flex-shrink-0">
                              {pet.name[0]}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{pet.name}</h3>
                              <p className="text-sm text-gray-600">
                                {pet.species} • {pet.breed || 'Mixed'} • {pet.gender || 'Unknown'}
                              </p>
                              {pet.date_of_birth && (
                                <p className="text-sm text-gray-500">
                                  {calculateAge(pet.date_of_birth)} old
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Behavioral Tags */}
                          {activeTags.length > 0 && (
                            <div className="mb-3">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Temperament
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {activeTags.map((tag) => (
                                  <div
                                    key={tag.key}
                                    className={`px-4 py-2.5 text-sm font-bold rounded-lg ${
                                      BEHAVIORAL_TAG_COLORS[tag.key as keyof typeof BEHAVIORAL_TAG_COLORS]
                                    }`}
                                  >
                                    {tag.label.toUpperCase()}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Special Instructions */}
                          {pet.behavioral_tags?.notes && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Special Instructions
                              </p>
                              <p className="text-sm text-gray-700 italic">
                                "{pet.behavioral_tags.notes}"
                              </p>
                            </div>
                          )}

                          {/* Encounter Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click
                              handlePetClick(pet.id);
                            }}
                            disabled={encounterLoading}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {encounterLoading ? 'Loading...' : appointment.status === AppointmentStatus.COMPLETE ? 'View Encounter' : 'Start Encounter'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Visit Reason / Notes */}
              {(appointment.description || appointment.notes) && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Visit Notes
                    </h3>
                  </div>
                  <div className="overflow-hidden">
                    <table className="min-w-full">
                      <tbody className="divide-y divide-gray-200">
                        {appointment.description && (
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50 w-1/3 align-top">
                              Reason for Visit
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {appointment.description}
                            </td>
                          </tr>
                        )}
                        {appointment.notes && (
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50 align-top">
                              Internal Notes
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {appointment.notes}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Appointment & Owner Info */}
            <div className="space-y-6">
              {/* Appointment Details Table */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Appointment Details
                  </h3>
                </div>
                <div className="overflow-hidden">
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50 w-1/3">
                          Time
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatTime(appointment.appointment_date)}
                          </div>
                          <div className="text-xs text-gray-500">{appointment.duration_minutes} minutes</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                          Type
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                          {appointment.appointment_type}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                          Status
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                          {appointment.status.replace('_', ' ')}
                        </td>
                      </tr>
                      {owner && (
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                            Owner
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                            {owner.full_name}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
